// RigSentinel — Drilling Fluid Management (Phase RCE-3)
//
// MUD PROGRAM — master record (parallel to Well Profile / BHA / Drill String).
// Storage key: `rsc.mudPrograms.v1`. Scoped by `wellId`.
//
// One Mud Program describes the planned (TARGET) mud properties for a well,
// optionally broken down by hole section. The `intervals` array stores
// per-section tri-state property tables; for simple programs the engineer
// can use a single global interval.

import { MUD_PROPERTY_KEYS, emptyPropertyTable, validateTriState } from "./properties";
import { scoped } from "@/lib/accountScope";

export const MUD_PROGRAM_SCHEMA_VERSION = 1;
export const MUD_PROGRAM_STORAGE_KEY    = "rsc.mudPrograms.v1";

export const FLUID_BASES = ["water-based", "oil-based", "synthetic-based", "other"];
export const MUD_PROGRAM_STATUSES = ["draft", "approved", "in-use", "archived"];

const _now = () => new Date().toISOString();
const _genId = () =>
  `mudprog_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
const _safeParse = (s, f) => { try { return JSON.parse(s) ?? f; } catch { return f; } };

export function emptyMudProgramInterval(label = "Whole well") {
  return {
    id: "",
    label,
    holeSection: "",
    topDepthFt: "",
    bottomDepthFt: "",
    properties: emptyPropertyTable(),
    notes: "",
  };
}

export function emptyMudProgram() {
  return {
    id: "",
    schemaVersion: MUD_PROGRAM_SCHEMA_VERSION,
    wellId: "",
    name: "",
    fluidBasis: "water-based",
    status: "draft",
    intervals: [emptyMudProgramInterval()],
    notes: "",
    isActive: true,
    createdAt: null,
    updatedAt: null,
  };
}

export function validateMudProgram(rec) {
  if (!rec || typeof rec !== "object")
    return { ok: false, errors: ["Mud Program is missing."] };
  const errs = [];
  if (!rec.wellId) errs.push("Mud Program must be linked to a well.");
  if (!rec.name || !String(rec.name).trim())
    errs.push("Mud Program name is required.");
  if (!FLUID_BASES.includes(rec.fluidBasis))
    errs.push(`Fluid basis must be one of: ${FLUID_BASES.join(", ")}.`);
  if (!MUD_PROGRAM_STATUSES.includes(rec.status))
    errs.push(`Status must be one of: ${MUD_PROGRAM_STATUSES.join(", ")}.`);
  if (!Array.isArray(rec.intervals) || rec.intervals.length === 0)
    errs.push("Mud Program must have at least one interval.");
  else {
    rec.intervals.forEach((iv, i) => {
      const tag = `Interval ${i + 1}`;
      if (!iv.properties || typeof iv.properties !== "object")
        errs.push(`${tag}: properties table is missing.`);
      else {
        for (const key of MUD_PROPERTY_KEYS) {
          const v = validateTriState(iv.properties[key], key === "oilWaterRatio");
          if (!v.ok) errs.push(`${tag} · ${key}: ${v.errors.join("; ")}`);
        }
      }
    });
  }
  return { ok: errs.length === 0, errors: errs };
}

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------
function _readAll() { return _safeParse(scoped.getItem(MUD_PROGRAM_STORAGE_KEY), []); }
function _writeAll(rows) { scoped.setItem(MUD_PROGRAM_STORAGE_KEY, JSON.stringify(rows)); }

function _validateOrThrow(rec) {
  const v = validateMudProgram(rec);
  if (!v.ok) {
    const e = new Error("Mud Program failed validation.");
    e.validationErrors = v.errors;
    throw e;
  }
}

export function createMudProgram(payload) {
  const base = { ...emptyMudProgram(), ...(payload || {}) };
  base.id = _genId();
  base.createdAt = _now();
  base.updatedAt = base.createdAt;
  base.isActive = true;
  _validateOrThrow(base);
  const all = _readAll();
  all.push(base);
  _writeAll(all);
  return base;
}

export function getMudProgram(id) {
  return _readAll().find((r) => r.id === id) || null;
}

export function listMudProgramsForWell(wellId, { includeInactive = false } = {}) {
  return _readAll()
    .filter((r) => r.wellId === wellId && (includeInactive || r.isActive !== false))
    .sort((a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")));
}

export function updateMudProgram(id, patch) {
  const all = _readAll();
  const i = all.findIndex((r) => r.id === id);
  if (i < 0) throw new Error(`Mud Program ${id} not found.`);
  const merged = {
    ...all[i],
    ...patch,
    id: all[i].id,
    createdAt: all[i].createdAt,
    wellId: all[i].wellId,    // wellId is immutable
  };
  merged.updatedAt = _now();
  _validateOrThrow(merged);
  all[i] = merged;
  _writeAll(all);
  return merged;
}

export function deleteMudProgram(id) {
  const all = _readAll();
  const next = all.filter((r) => r.id !== id);
  _writeAll(next);
  return all.length - next.length;
}

// ---------------------------------------------------------------------------
// Active Mud Program resolution
// ---------------------------------------------------------------------------

/**
 * Pick the active Mud Program for a well. Resolution order:
 *   1) explicit programId in ctx (e.g. EngineeringSession.mudProgramId)
 *   2) program with status === "in-use" for that well
 *   3) most recently updated active program for that well
 *
 * Returns the program record or null.
 */
export function resolveActiveMudProgram(wellId, ctx = {}) {
  if (!wellId) return null;
  if (ctx.mudProgramId) {
    const explicit = getMudProgram(ctx.mudProgramId);
    if (explicit && explicit.wellId === wellId) return explicit;
  }
  const candidates = listMudProgramsForWell(wellId);
  const inUse = candidates.find((p) => p.status === "in-use");
  return inUse || candidates[0] || null;
}

export function _clearAllMudPrograms() {
  scoped.removeItem(MUD_PROGRAM_STORAGE_KEY);
}
